import type { FC } from "react";
import { Fragment, memo, useCallback, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { colorsPalettes } from "@similarweb/styles";
import { Button, IconButton } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { toggleEducationBar } from "app/actions/educationBarActions";
import { parsedArticle, useGetEducationDataForPage } from "app/components/educationbar/education-utils";
import { ArticlesService } from "app/components/React/EducationBarContainer/ArticlesService";
import { translate } from "app/filters/i18nFilter";
import { getDefaultParamsForState } from "app/pages/digital-suite/home-page/helpers";
import { KeywordsGroupEditorModal } from "app/pages/keyword-analysis/KeywordsGroupEditorModal";
import { SegmentsPageLoader } from "app/pages/segments/segmentsPageLoader";
import { useAllKeywordGroups } from "app/services/keyword-groups/hooks";
import type { GoogleAdsImportedLinkage } from "app/services/keyword-groups/utils/googleAdsImportedKeywordGroups";
import { addGoogleAdsImportedKeywordGroupId } from "app/services/keyword-groups/utils/googleAdsImportedKeywordGroups";
import { RecentService } from "app/services/recent/recentService";
import { allTrackers } from "app/services/track/track";
import type { GetAccountUsersResponse } from "app/sharing/SharingService";
import { SharingService } from "app/sharing/SharingService";
import { KeywordGroupCreationDropdown } from "pro-features/components/GroupCreationDropdown/src/KeywordGroupCreationDropdown";
import { FlexColumn, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useMonitorKeywordsNavigationTargets } from "../monitorKeywordsNavigationTargets";
import type { IKeywordGroup } from "app/common/keyword-research/monitor-lists/monitorKeywordsTypes";
import { getSharedGroupsWithOwnerName, hasGoogleAds, removeOwnerGroupsFromSharedGroups } from "../utils";

import { GoogleAdsImportWizard } from "./google-ads-import/google-ads-import-wizard";
import { MonitorKeywordsStartPage } from "./MonitorKeywordsStartPage";
import type { GroupType } from "./monitorKeywordsStartPageTypes";
import {
    KeywordGroupsPageContainer,
    KeywordGroupsPageContentContainer,
    KeywordsEmptyImage,
    NoKeywordGroupsContainer,
} from "./styledComponents";

interface IMonitorKeywordsStartPageContainerProps {
    monitorKeywordsState?: string;
}

const KeywordGroupCreationDropdownWrapper: FC<{
    onGoToGeneratorToolClick: VoidFunction;
    onCreateNewListClick: VoidFunction;
    onClickCreateList: VoidFunction;
}> = memo(function KeywordGroupCreationDropdownWrapper({
    onGoToGeneratorToolClick,
    onCreateNewListClick,
    onClickCreateList,
}) {
    return (
        <KeywordGroupCreationDropdown
            hasKeywordsGenerator={true}
            onGeneratorToolClick={onGoToGeneratorToolClick}
            onKeywordsModalClick={onCreateNewListClick}
            dropdownPopupPlacement={"ontop-right"}
            bubbleDirection={"right"}
            hideNotification={true}
            appendTo={"body"}
        >
            <IconButton
                key="button-1"
                iconName="add"
                onClick={onClickCreateList}
                dataAutomation="create-new-kwl"
            >
                {translate("keywordresearch.monitorkeywords.button.create")}
            </IconButton>
        </KeywordGroupCreationDropdown>
    );
});

export const MonitorKeywordsStartPageContainer: FC<IMonitorKeywordsStartPageContainerProps> = () => {
    const [isOpen, setIsOpen] = useState<boolean>();
    const isGoogleAdsEnabled = hasGoogleAds();
    const [isGoogleAdsImportOpen, setIsGoogleAdsImportOpen] = useState(false);
    const dispatch = useDispatch();
    const educationData = useGetEducationDataForPage();
    const navTargets = useMonitorKeywordsNavigationTargets();

    const { data: usersData = [], isLoading: isUsersLoading } = useQuery({
        queryKey: ["user-accounts"],
        queryFn: () => SharingService.getAccountUsers(),
        select: (users: GetAccountUsersResponse) => {
            if (users.err || !users.users) {
                return [];
            }

            return users.users;
        },
    });

    const { userGroups, sharedGroups, isLoading: isGroupLoading } = useAllKeywordGroups();

    const openZenDesk = useCallback(() => {
        if (!educationData?.zendesk) {
            return;
        }

        void ArticlesService.GetArticleById(educationData.zendesk).then((res) => {
            const parsed = parsedArticle(res);
            dispatch(toggleEducationBar(true, parsed));
        });
    }, [dispatch, educationData]);

    const sharedKeywordGroupsWithOwnerName = useMemo(() => {
        if (isGroupLoading || isUsersLoading) {
            return [];
        }
        const sharedGroupsWithoutOwnerGroups = removeOwnerGroupsFromSharedGroups(sharedGroups, userGroups);
        return getSharedGroupsWithOwnerName(sharedGroupsWithoutOwnerGroups as IKeywordGroup[], usersData);
    }, [isGroupLoading, isUsersLoading, sharedGroups, userGroups, usersData]);

    const navigateToMonitorKeywords = useCallback(
        <T extends Pick<GroupType, "Id">>(record: T) => {
            void proNavigator.go(
                navTargets.monitorKeywordsState,
                {
                    keywordGroupId: record.Id,
                    duration: "12m",
                    country: 999,
                    webSource: "Total",
                    keywordsType: "both",
                },
                {
                    reload: true,
                },
            );
        },
        [navTargets.monitorKeywordsState],
    );

    const onGoToGeneratorToolClick = useCallback(() => {
        void proNavigator
            .go(navTargets.keywordGeneratorState, {
                ...getDefaultParamsForState(navTargets.keywordGeneratorState),
            })
            .then(() => {
                allTrackers.dispatchTrackEvent("Navigation", "click", "Go to Generator Tool");
            });
    }, [navTargets.keywordGeneratorState]);

    const onCreateNewListClick = useCallback(() => {
        allTrackers.dispatchTrackEvent("Button", "click", "enter keyword list");
        const isQueryLimitReached = RecentService.isQueryLimiterEnabled();
        if (isQueryLimitReached) {
            RecentService.getQueryLimiterHook();
        } else {
            setIsOpen(true);
        }
    }, []);

    const onClickCreateList = useCallback(() => {
        allTrackers.dispatchTrackEvent("Button", "click", "Create new list clicked");
    }, []);

    const onImportFromGoogleAdsClick = useCallback(() => {
        allTrackers.dispatchTrackEvent("Button", "click", "Import from Google Ads clicked");
        setIsGoogleAdsImportOpen(true);
    }, []);

    const onGoogleAdsImportClose = useCallback(() => {
        setIsGoogleAdsImportOpen(false);
    }, []);

    const onGoogleAdsImportSuccess = useCallback(
        (createdGroupId: string, linkage: GoogleAdsImportedLinkage) => {
            addGoogleAdsImportedKeywordGroupId(createdGroupId, linkage);
            setIsGoogleAdsImportOpen(false);
            navigateToMonitorKeywords({ Id: createdGroupId });
        },
        [navigateToMonitorKeywords],
    );

    const noDataExists = !isUsersLoading && !isGroupLoading && sharedGroups?.length === 0 && userGroups?.length === 0;

    if (isUsersLoading || isGroupLoading) {
        return <SegmentsPageLoader />;
    }

    return (
        <KeywordGroupsPageContainer>
            <KeywordGroupsPageContentContainer>
                {noDataExists ? (
                    <NoKeywordGroupsContainer>
                        <FlexColumn
                            gap="12px"
                            alignItems="center"
                        >
                            <Text
                                as="h4"
                                fontVariant="h4"
                            >
                                {translate("keyword.groups.home.noGroups.title")}
                            </Text>
                            <Text
                                color={colorsPalettes.carbon[500]}
                                fontVariant="body1"
                            >
                                {translate("keyword.groups.home.noGroups.description")}
                            </Text>
                        </FlexColumn>
                        <FlexColumn
                            gap="12px"
                            alignItems="center"
                        >
                            <KeywordGroupCreationDropdownWrapper
                                onClickCreateList={onClickCreateList}
                                onCreateNewListClick={onCreateNewListClick}
                                onGoToGeneratorToolClick={onGoToGeneratorToolClick}
                            />
                            <Button
                                type="flat"
                                onClick={openZenDesk}
                            >
                                {translate("global.needHelp")}
                            </Button>
                        </FlexColumn>
                        <KeywordsEmptyImage />
                    </NoKeywordGroupsContainer>
                ) : (
                    <Fragment>
                        <FlexRow
                            gap="8px"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Text
                                as="h4"
                                fontVariant="h4"
                                color={colorsPalettes.carbon[500]}
                                data-automation="keyword-list-title"
                            >
                                {translate("research.homepage.keywordgroups.title")}
                            </Text>
                            <FlexRow gap="8px">
                                {educationData?.zendesk ? (
                                    <IconButton
                                        iconName="learning-hat"
                                        type="flat"
                                        onClick={openZenDesk}
                                    />
                                ) : null}
                                {isGoogleAdsEnabled ? (
                                    <IconButton
                                        iconName="google"
                                        onClick={onImportFromGoogleAdsClick}
                                        dataAutomation="kwl-import-google-ads"
                                    >
                                        Import from Google Ads
                                    </IconButton>
                                ) : null}
                                <KeywordGroupCreationDropdownWrapper
                                    onClickCreateList={onClickCreateList}
                                    onCreateNewListClick={onCreateNewListClick}
                                    onGoToGeneratorToolClick={onGoToGeneratorToolClick}
                                />
                            </FlexRow>
                        </FlexRow>
                        <MonitorKeywordsStartPage
                            keywordGroups={userGroups as IKeywordGroup[]}
                            sharedKeywordGroups={sharedKeywordGroupsWithOwnerName}
                            users={usersData}
                            isLoading={isUsersLoading || isGroupLoading}
                            navigationTargets={navTargets}
                        />
                    </Fragment>
                )}
                <KeywordsGroupEditorModal
                    onClose={() => setIsOpen(false)}
                    open={isOpen}
                    showGoToMonitorKeywordListButton
                    onSave={(modifiedGroup) => navigateToMonitorKeywords(modifiedGroup)}
                />
                {isGoogleAdsEnabled ? (
                    <GoogleAdsImportWizard
                        isOpen={isGoogleAdsImportOpen}
                        onClose={onGoogleAdsImportClose}
                        onImported={onGoogleAdsImportSuccess}
                    />
                ) : null}
            </KeywordGroupsPageContentContainer>
        </KeywordGroupsPageContainer>
    );
};
